import ClearIcon from "@mui/icons-material/Clear";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Skeleton from "@mui/material/Skeleton";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { lightScrollbarStyle } from "../../../../styles/lightScrollbarStyle";
import {
  treeSearchBaseChipStyles,
  treeSearchInputAdornmentStyles,
  treeSearchInputListboxStyles,
  treeSearchInternalInputStyles,
  treeSearchMoreChipStyles,
  treeSearchOutlinedInputStyles,
} from "./styles";

interface TreeSearchInputProps {
  selectedItems: string[];
  renderPopperContent: React.ReactNode;
  placeholder?: string;
  value: string; // Controlled value for the input
  clear: () => void;
  onInputChange?: (value: string) => void;
  onChipDelete?: (chipId: string) => void;
  disabled?: boolean;
  minCharsToShowPopper?: number;
  maxVisibleChips?: number;
  getItemLabel?: (itemId: string) => string;
  id?: string;
  onClose?: () => void;
  loading?: boolean;
  showDoneButton?: boolean;
}

export const TreeSearchInput: React.FC<TreeSearchInputProps> = ({
  selectedItems = [],
  placeholder = "Search...",
  value,
  clear,
  onInputChange,
  onChipDelete,
  renderPopperContent,
  minCharsToShowPopper = 0, // Default to 0 to show immediately
  maxVisibleChips = 10, // Default to 10 visible chips
  getItemLabel = (id) => id,
  id = "tree-search-input",
  onClose,
  loading = false,
  showDoneButton = false,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when popper opens
  useEffect(() => {
    if (!(open && inputRef.current)) return;

    inputRef.current.focus();
  }, [open]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      if (onInputChange) onInputChange(newValue);

      setOpen(newValue.length >= minCharsToShowPopper);
    },
    [onInputChange, minCharsToShowPopper]
  );

  const handleDeleteChip = useCallback(
    (chipId: string) => {
      if (!onChipDelete) return;

      onChipDelete(chipId);
    },
    [onChipDelete]
  );

  const handleInputClick = useCallback(() => {
    if (value.length < minCharsToShowPopper && !open) return;

    setOpen((prevOpen) => !prevOpen);

    // Focus after opening
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [value, minCharsToShowPopper, open]);

  const handlePaperClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      inputRef.current?.focus();
    },
    []
  );

  const handleClear = useCallback(() => {
    clear();
  }, [clear]);

  const handleTogglePopper = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setOpen((prevOpen) => !prevOpen);
    },
    []
  );

  const handleClose = useCallback(() => {
    setOpen((open) => {
      if (open) {
        //only call onClose when popper is open
        onClose?.();
        return !open;
      }
      return open;
    });
  }, [onClose]);

  const renderChips = useCallback(() => {
    if (selectedItems.length === 0) return null;

    // Show skeleton chips based on number of selected items
    if (loading) {
      return (
        <>
          {selectedItems.slice(0, maxVisibleChips).map((_, index) => (
            <Skeleton
              key={`skeleton-${index}`}
              variant="rounded"
              width={80}
              height={24}
              sx={{
                borderRadius: "16px",
                m: "2px",
              }}
            />
          ))}
          {selectedItems.length > maxVisibleChips && (
            <Skeleton
              key="skeleton-more"
              variant="rounded"
              width={60}
              height={24}
              sx={{
                borderRadius: "16px",
                m: "2px",
              }}
            />
          )}
        </>
      );
    }

    return (
      <>
        {selectedItems.slice(0, maxVisibleChips).map((item, index) => (
          <Chip
            key={item}
            id={`${id}-chip-${index}`}
            data-testid={`${id}-chip-${index}`}
            label={getItemLabel(item)}
            size="small"
            tabIndex={-1}
            onDelete={() => handleDeleteChip(item)}
            sx={treeSearchBaseChipStyles}
          />
        ))}
        {selectedItems.length > maxVisibleChips && (
          <Chip
            key="more"
            id={`${id}-more-chip`}
            data-testid={`${id}-more-chip`}
            label={`+${selectedItems.length - maxVisibleChips} more`}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
            sx={treeSearchMoreChipStyles}
          />
        )}
      </>
    );
  }, [
    selectedItems,
    maxVisibleChips,
    handleDeleteChip,
    getItemLabel,
    id,
    setOpen,
    loading,
  ]);

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: "relative", width: "100%" }} ref={anchorRef} id={id}>
        <OutlinedInput
          id={`${id}-input`}
          fullWidth
          autoComplete="off"
          inputRef={inputRef}
          placeholder={value.length === 0 ? placeholder : ""}
          value={value}
          onChange={handleInputChange}
          sx={treeSearchOutlinedInputStyles}
          startAdornment={renderChips()}
          endAdornment={
            <Box sx={treeSearchInputAdornmentStyles}>
              {selectedItems.length > 0 && (
                <IconButton
                  id={`${id}-clear-btn`}
                  data-testid={`${id}-clear-btn`}
                  aria-label="clear input"
                  onClick={handleClear}
                  size="small"
                  edge="end"
                  tabIndex={-1}
                  sx={{ visibility: "visible", mr: 0.5 }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                id={`${id}-toggle-btn`}
                aria-label="toggle dropdown"
                data-testid={`${id}-toggle-btn`}
                onClick={handleTogglePopper}
                size="small"
                edge="end"
                tabIndex={-1}
                sx={{
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                <ArrowDropDownIcon />
              </IconButton>
            </Box>
          }
          inputProps={{
            size: "small",
            sx: treeSearchInternalInputStyles,
            "data-testid": `${id}-input`,
          }}
          onClick={handleInputClick}
        />

        <Popper
          id={`${id}-popper`}
          open={open && !!renderPopperContent}
          anchorEl={anchorRef.current}
          placement="bottom"
          modifiers={[
            { name: "offset", options: { offset: [0, 0] } },
            {
              name: "matchWidth",
              enabled: true,
              phase: "beforeWrite",
              requires: ["computeStyles"],
              fn: ({ state }) => {
                state.styles.popper.width = `${state.rects.reference.width}px`;
              },
              effect: ({ state }) => {
                const referenceElement = state.elements
                  .reference as HTMLElement;
                state.elements.popper.style.width = `${referenceElement.offsetWidth}px`;
              },
            },
            {
              name: "preventOverflow",
              enabled: true,
              options: {
                altAxis: true,
                boundary: "viewport",
              },
            },
          ]}
          sx={{ zIndex: 1500 }}
        >
          <Paper
            id={`${id}-paper`}
            elevation={2}
            sx={{
              ...treeSearchInputListboxStyles,
              ...lightScrollbarStyle,
              ...(showDoneButton && {
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }),
            }}
            onClick={handlePaperClick}
          >
            {showDoneButton ? (
              <>
                <Box
                  sx={{
                    flex: 1,
                    overflow: "auto",
                    ...lightScrollbarStyle,
                  }}
                >
                  {renderPopperContent}
                </Box>
                <Divider />
                <Button
                  fullWidth
                  onClick={() => setOpen(false)}
                  id={`${id}-done-btn`}
                  data-testid={`${id}-done-btn`}
                  sx={{ py: 1, fontWeight: 700, flexShrink: 0 }}
                >
                  DONE
                </Button>
              </>
            ) : (
              renderPopperContent
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};
